import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";
import { Button } from "../components/ui/button";

import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

import PMSSoftwarePricingComparisonSlide from "../components/slides-ai-os/PMSSoftwarePricingComparisonSlide";
import PricingComparisonV3Slide from "../components/slides-ai-os/PricingComparisonV3Slide";
import PMSPiecemealTotalSlide from "../components/slides-ai-os/PMSPiecemealTotalSlide";
import EngagementPricingSlide from "../components/slides-ai-os/EngagementPricingSlide";
import AICreditConsumptionSlide from "../components/slides-ai-os/AICreditConsumptionSlide";

const ACCESS_KEY = "asha_ai_os_pricing_deck_access";

const slides = [
  { id: "software-pricing-comparison", title: "Software pricing comparison", Component: PMSSoftwarePricingComparisonSlide },
  { id: "ai-pricing-comparison", title: "AI pricing comparison", Component: PricingComparisonV3Slide },
  { id: "piecemeal-total", title: "What piecemealing costs", Component: PMSPiecemealTotalSlide },
  { id: "engagement-pricing", title: "Pricing", Component: EngagementPricingSlide },
  { id: "ai-credit-consumption", title: "How AI credit consumption works", Component: AICreditConsumptionSlide },
];

const AIOSPricingDeckPage = () => {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [scale, setScale] = useState(1);
  const [isLocked, setIsLocked] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    if (hasStoredContentAccess(ACCESS_KEY)) setIsLocked(false);
  }, []);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("Deck access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    document.title = "Asha AI OS — Pricing Deck";
    const previousBg = document.body.style.background;
    document.body.style.background = "#0a0a0a";
    return () => {
      document.body.style.background = previousBg;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      const parentWidth = containerRef.current.offsetWidth;
      const newScale = parentWidth / 1400;
      setScale(newScale);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!api) return;
    const handler = (e) => {
      const tag = e.target && e.target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (e.target && e.target.isContentEditable)
      ) {
        return;
      }
      if (e.key === "ArrowRight" || e.key === "PageDown") {
        api.scrollNext();
        e.preventDefault();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        api.scrollPrev();
        e.preventDefault();
      } else if (e.key === "Home") {
        api.scrollTo(0);
        e.preventDefault();
      } else if (e.key === "End") {
        api.scrollTo(slides.length - 1);
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [api]);

  return (
    <div className="min-h-screen xl:h-screen w-screen bg-neutral-950 flex flex-col items-center p-3 md:p-6 overflow-x-hidden">
      {isLocked && (
        <EmailGate
          onAccessGranted={handleAccessGranted}
          sessionStorageKey={ACCESS_KEY}
        />
      )}

      {/* Top bar */}
      <div className="w-full max-w-[1800px] flex items-center justify-between gap-3 mb-4 z-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg overflow-hidden bg-white/5 border border-white/10 backdrop-blur-sm flex items-center justify-center p-1.5">
            <img
              src="/assets/alogo.png"
              alt="Asha"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="leading-tight">
            <div className="text-[13px] font-semibold text-white tracking-tight">
              Asha Health
            </div>
            <div className="text-[10.5px] uppercase tracking-[0.16em] text-white/40 font-mono">
              AI OS · Pricing
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-white/40">
          <span className="text-white/30">Confidential</span>
          <span className="text-white/20">·</span>
          <span className="text-white/30">Pricing deck</span>
        </div>
      </div>

      {/* Main slide container */}
      <div className="w-full max-w-[1800px] flex-1 flex items-center justify-center min-h-0">
        <div
          ref={containerRef}
          className="w-full max-w-[1400px] aspect-video relative"
        >
          <div className="w-full h-full overflow-hidden bg-transparent relative rounded-3xl shadow-2xl ring-1 ring-white/10">
            <div
              style={{
                width: "1400px",
                height: "787.5px",
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <Carousel setApi={setApi} className="w-full h-full">
                <CarouselContent className="h-full ml-0">
                  {slides.map((s) => (
                    <CarouselItem key={s.id} className="h-full pl-0">
                      <s.Component />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="w-full max-w-[1400px] flex items-center justify-between gap-3 mt-4 z-50">
        <Button
          variant="outline"
          onClick={() => api?.scrollPrev()}
          disabled={current === 0}
          className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-white/5"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={`Go to slide ${i + 1}: ${s.title}`}
                onClick={() => api?.scrollTo(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === current
                    ? "w-6 bg-blue-400"
                    : "w-1.5 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-white/50 whitespace-nowrap">
            {String(current + 1).padStart(2, "0")} /{" "}
            {String(slides.length).padStart(2, "0")}{" "}
            <span className="text-white/30">·</span>{" "}
            <span className="text-white/80">{slides[current]?.title}</span>
          </div>
        </div>

        <Button
          onClick={() => api?.scrollNext()}
          disabled={current === slides.length - 1}
          className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-30 disabled:hover:bg-blue-600"
        >
          Next
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default AIOSPricingDeckPage;
